import React, {
  ChangeEvent,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Switch, Form, Input, DatePicker, Button, Spin, Select } from 'antd';
import {
  SortDescendingOutlined,
  SortAscendingOutlined,
  ClearOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useStore } from 'effector-react';
import moment from 'moment';
import css from './SpamUsersFilters.module.less';
import spamUsers, {
  getList,
  DEFAULT_SPAM_USERS_FILTERS,
  DEFAULT_SPAM_USERS_SORT,
} from '@stores/spamUsers/spamUsers';
import useTranslate from '@hooks/useTranslate';
import componentContent from './SpamUsersFilters.content.json';
import localeWeightSpamUsers, {
  setLocaleWeightSpamUsers,
  LOCALE,
  localeWeightMap,
} from '@stores/spamUsers/localeWeightSpamUsers';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface SpamUsersFiltersProps {}

const SpamUsersFilters: FunctionComponent<SpamUsersFiltersProps> = () => {
  const { filters, preloaders } = useStore(spamUsers);
  const localeName = useStore(localeWeightSpamUsers);

  const [form] = Form.useForm();
  const [dateSortType, setDateSortType] = useState<string>('desc');
  const [claimSortType, setClaimSortType] = useState<string>('desc');
  const [registrationSortType, setRegistrationSortType] = useState<string>('desc');
  const [isDispalyBlocked, setIsDispalyBlocked] = useState<boolean>(false);
  const [nicknameSearch, setNickname] = useState('');

  const content = useTranslate(componentContent);

  useEffect(() => {
    const currentSort = Array.isArray(filters.sort) ? filters.sort : DEFAULT_SPAM_USERS_SORT;

    const registrationSort = currentSort[0]?.split(',')[1] || 'desc';
    const claimSort = currentSort[1]?.split(',')[1] || 'desc';
    const dateSort = currentSort[2]?.split(',')[1] || 'desc';

    setRegistrationSortType(registrationSort);
    setClaimSortType(claimSort);
    setDateSortType(dateSort);

    const nextDisplayBlocked = Boolean(filters.showBlocked && filters.hideUnblocked);
    setIsDispalyBlocked(nextDisplayBlocked);
    setNickname(filters.nickname || '');

    form.setFieldsValue({
      nickname: filters.nickname || '',
      date: [
        filters.createdFrom ? moment(filters.createdFrom) : undefined,
        filters.createdTo ? moment(filters.createdTo) : undefined,
      ],
    });
  }, [filters, form]);

  const isSearchByBlockedFilter = useMemo(() => {
    return Boolean(filters.nickname);
  }, [filters.nickname]);

  const onNicknameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNickname(e.currentTarget.value);
  };

  const searchByNickname = (nickname: string) => {
    getList({
      filters: {
        ...filters,
        nickname: nickname || undefined,
      },
    });
  };

  const changeSortHandler = useCallback((sortType, setSortType) => {
    setSortType(sortType === 'desc' ? 'asc' : 'desc');
  }, []);

  const mapFormFields = ({ date }: any) => {
    return {
      nickname: nicknameSearch || undefined,
      createdFrom: date?.[0]?.format(),
      createdTo: date?.[1]?.format(),
      checkedType: isDispalyBlocked ? 'CHECKED' : 'UNCHECKED',
      showBlocked: isDispalyBlocked,
      hideUnblocked: isDispalyBlocked,
    };
  };

  const onFinishHandler = useCallback(
    (values) => {
      getList({
        filters: {
          ...filters,
          ...mapFormFields(values),
        },
        sort: [
          `registrationDate,${registrationSortType}`,
          `accountClaimsCount,${claimSortType}`,
          `claimDate,${dateSortType}`,
        ],
      });
    },
    [filters, nicknameSearch, isDispalyBlocked, registrationSortType, claimSortType, dateSortType],
  );

  const onChangeLocale = (newLocale: LOCALE) => {
    setLocaleWeightSpamUsers(newLocale);
    getList();
  };

  const displayChecked = (checked: boolean) => {
    setIsDispalyBlocked(checked);

    getList({
      filters: {
        ...filters,
        checkedType: checked ? 'CHECKED' : 'UNCHECKED',
        showBlocked: checked,
        hideUnblocked: checked,
      },
    });
  };

  const clearFilters = useCallback(() => {
    setNickname('');
    setDateSortType('desc');
    setClaimSortType('desc');
    setRegistrationSortType('desc');
    setIsDispalyBlocked(false);

    form.setFieldsValue({
      nickname: '',
      date: [undefined, undefined],
    });

    setLocaleWeightSpamUsers(LOCALE.ALL);

    getList({
      filters: {
        ...DEFAULT_SPAM_USERS_FILTERS,
        nickname: undefined,
        createdFrom: undefined,
        createdTo: undefined,
      },
      sort: DEFAULT_SPAM_USERS_SORT,
    });
  }, [form]);

  return (
    <Form
      layout='vertical'
      form={form}
      onFinish={(values) => onFinishHandler(values)}
    >
      <Spin spinning={!!preloaders.getList}>
        <Form.Item
          label={content.byNicknameLabel}
          name='nickname'
        >
          <Input.Search
            allowClear
            value={nicknameSearch}
            onSearch={searchByNickname}
            onChange={onNicknameChange}
          />
        </Form.Item>

        <Form.Item label={content.locale}>
          <Select
            value={localeName}
            style={{ width: 120 }}
            onChange={onChangeLocale}
          >
            {localeWeightMap &&
              Object.entries(localeWeightMap).map(([key, value]) => {
                return (
                  <Option
                    value={key}
                    key={key}
                  >
                    {value.name}
                  </Option>
                );
              })}
          </Select>
        </Form.Item>

        <div className={css.date}>
          <Button
            onClick={() => changeSortHandler(dateSortType, setDateSortType)}
            type='link'
            style={{ margin: 0, padding: 0 }}
          >
            <span>{content.date}</span>
            {dateSortType === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          </Button>

          <Form.Item name='date'>
            <RangePicker
              showTime={{
                defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('00:00:00', 'HH:mm:ss')],
              }}
              format='DD-MM-YYYY HH:mm:ss'
              allowEmpty={[true, true]}
              placeholder={[content.rangePlaceholderFrom, content.rangePlaceholderTo]}
            />
          </Form.Item>
        </div>

        <Form.Item>
          <Button
            onClick={() => changeSortHandler(claimSortType, setClaimSortType)}
            type='link'
            style={{ margin: 0, padding: 0 }}
          >
            <span>{content.accountClaimsCount}</span>
            {claimSortType === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          </Button>
        </Form.Item>

        <Form.Item>
          <Button
            onClick={() => changeSortHandler(registrationSortType, setRegistrationSortType)}
            type='link'
            style={{ margin: 0, padding: 0 }}
          >
            <span>Сортировка по дате регистрации</span>
            {registrationSortType === 'desc' ? (
              <SortDescendingOutlined />
            ) : (
              <SortAscendingOutlined />
            )}
          </Button>
        </Form.Item>

        <Form.Item
          label={content.displayBlocked}
          name='displayBlocked'
          valuePropName='checked'
        >
          <Switch
            checked={isDispalyBlocked}
            disabled={isSearchByBlockedFilter}
            onChange={displayChecked}
            loading={!!preloaders.getList}
          />
        </Form.Item>

        <div className={css.formFooter}>
          <Button
            htmlType='submit'
            icon={<SearchOutlined />}
            type='primary'
            loading={!!preloaders.getList}
          >
            {content.search}
          </Button>

          <Button
            htmlType='button'
            icon={<ClearOutlined />}
            onClick={clearFilters}
            loading={!!preloaders.getList}
          >
            {content.clear}
          </Button>
        </div>
      </Spin>
    </Form>
  );
};

export default SpamUsersFilters;
